import {
  Component, ChangeDetectionStrategy, Input, ElementRef, ViewChild, AfterViewInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';


import { isNullOrUndefined } from '@app/util/util';
import { HomeSection, HomeFlightOffers } from '@shared/home-sections/entities/home-data.response';
import { SectionType, SECTION_TYPE_NAME } from '@shared/home-sections/entities/section-type';
import { Promo, Store, Tile, Banner, HotelPromo } from '@shared/sliders/entities/slide-models';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { User } from '@app/modules/core/interfaces/user';
import { AppFacade } from '@app/app.facade';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { HomeItemDetailWidgetType } from '@home-item-detail/entities/home-item-detail';
import { FinancialPartnersInfoVM } from '@shared/benefits/entities/benefits';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import {
  TypographyFontSize, TypographyColor, TypographyFontFamily
} from '@design-system/components/atoms/typography/entities/typography.types';

const rappiCardImage = 'RappiCard_Image.png';
const queryParamaHtmlId = 'htmlid';
const HALF_ONE_SECOND_MILLISECONDS = 500;
@Component({
  selector: 'app-home-section-list',
  templateUrl: './home-section-list.component.html',
  styleUrls: ['./home-section-list.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HomeSectionListComponent implements AfterViewInit {

  @ViewChild('modalWebView') modalWebView: ElementRef;
  @ViewChild('iframeWebView') iframeWebView: ElementRef;
  @ViewChild('homeSectionList', { static: false }) homeSectionList: ElementRef;

  @Input() homeSections: HomeSection[] = [];
  @Input() flightOffers: HomeFlightOffers[] = [];
  @Input() source: EVENT_SOURCE | string;
  @Input() branchCode: string;
  @Input() isMobile: boolean;
  @Input() titleOffersFlight: string;
  @Input() departurePlace: AutocompletePlace;
  @Input() arrivalPlace: SelectAutocompletePlace;
  @Input() salesChannel: string = null;
  @Input() airline: string = null;
  @Input() pageCode: string = null;
  @Input() widgets: HomeItemDetailWidgetType[];

  public loadingIframe: boolean = true;
  public searchBySelected: SearchBy;
  public financialPartnersInfo: FinancialPartnersInfoVM;
  public showFinancialPartners = false;
  public iframeHasBeenRequested = false;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private appFacade: AppFacade,
    private router: Router,
    private route: ActivatedRoute,
    private readonly avoService: AvoService,
  ) { }

  ngAfterViewInit(): void {
    this.scrollToSection();
  }

  get isPrime$(): Observable<boolean> {
    return this.appFacade.isPrime$;
  }

  get hasRappiCard$(): Observable<boolean> {
    return this.appFacade.hasRappiCard$;
  }

  get rappiCardImage(): string {
    return rappiCardImage;
  }

  private get user$(): Observable<User> {
    return this.appFacade.user$;
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.appFacade.isLoggedIn$;
  }

  get sectionTypePromo(): SectionType {
    return SectionType.Promo;
  }

  get sectionTypeBanner(): SectionType {
    return SectionType.Banner;
  }

  get sectionTypeStore(): SectionType {
    return SectionType.Store;
  }

  get sectionTypeText(): SectionType {
    return SectionType.Text;
  }

  get sectionTypeTile(): SectionType {
    return SectionType.Tile;
  }

  get sectionTypeRecentSearch(): SectionType {
    return SectionType.RecentSearch;
  }

  get sectionTypeHotelPromo(): SectionType {
    return SectionType.HotelPromo;
  }

  get currentRoute(): string {
    return this.router.url.split('?')[0];
  }

  get flightOffersAreEnabled(): boolean {
    return !!this.flightOffers?.length && this.widgets?.includes(HomeItemDetailWidgetType.Air);
  }

  public isOnlyText(section: HomeSection): boolean {
    return Object.values(section).length > 1;
  }

  public sectionType(section: HomeSection): SectionType {
    if (!isNullOrUndefined(section.banners) && section.banners.length > 0) {
      return SectionType.Banner;
    } else if (!isNullOrUndefined(section.promos) && section.promos.length > 0) {
      return SectionType.Promo;
    } else if (!isNullOrUndefined(section.hotel_promos) && section.hotel_promos.length > 0) {
      return SectionType.HotelPromo;
    } else if (!isNullOrUndefined(section.stores) && section.stores.length > 0) {
      return SectionType.Store;
    } else if (!isNullOrUndefined(section.tiles) && section.tiles.length > 0) {
      return SectionType.Tile;
    } else if (!isNullOrUndefined(section.recentSearchs)) {
      return SectionType.RecentSearch;
    } else if (!isNullOrUndefined(section.text)) {
      return SectionType.Text;
    }
  }

  public sectionNotExclusiveRappiCard(section: HomeSection): boolean {
    return this.sectionType(section) !== SectionType.Text;
  }

  public clickPromo(promo: Promo, index: number): void {
    this.amplitudeEventSection(promo, index, SectionType.Promo);
  }

  public clickStore(store: Store, index: number): void {
    this.amplitudeEventSection(store, index, SectionType.Store);
  }

  public clickTile(tile: Tile, index: number): void {
    this.amplitudeEventSection(tile, index, SectionType.Tile);
  }

  public clickBanner(banner: Banner, index: number): void {
    const page_code = banner?.deeplink?.slice(banner.deeplink.lastIndexOf('/')) || '';
    this.registerAmplitudeEventForBanner(
      this.homeSections[index].title,
      page_code,
      index + 1,
      this.homeSections[index].banners.findIndex(x => x === banner) + 1,
      SECTION_TYPE_NAME.Banner
    );
  }

  public clickHotelPromo(hotelPromo: HotelPromo, index: number): void {
    this.registerAmplitudeEventForBanner(
      this.homeSections[index].title,
      hotelPromo.hotel_name,
      index + 1,
      this.homeSections[index].hotel_promos.findIndex(x => x === hotelPromo) + 1,
      SECTION_TYPE_NAME.HotelPromo
    );
  }

  public amplitudeEventSection(section: Promo | Store | Tile, index: number, sectionType: SectionType): void {
    let indexItem = 0;
    let sectionTypeName;
    const defaultIndex = 0;
    const homeSection = this.homeSections[index];
    switch (sectionType) {
      case SectionType.Promo:
        indexItem = isNullOrUndefined(homeSection.promos) ? defaultIndex : homeSection.promos.findIndex(x => x === section) + 1;
        sectionTypeName = SECTION_TYPE_NAME.Promo;
        break;
      case SectionType.Store:
        indexItem = isNullOrUndefined(homeSection.stores) ? defaultIndex : homeSection.stores.findIndex(x => x === section) + 1;
        sectionTypeName = SECTION_TYPE_NAME.Store;
        break;
      case SectionType.Tile:
        indexItem = isNullOrUndefined(homeSection.tiles) ? defaultIndex : homeSection.tiles.findIndex(x => x === section) + 1;
        sectionTypeName = SECTION_TYPE_NAME.Tile;
        break;
    }
    this.registerAmplitudeEventForBanner(homeSection.title, section.title, index + 1, indexItem, sectionTypeName);
  }

  public registerAmplitudeEventForBanner(titleSection: string, titlePromo: string, indexSection: number,
    indexPromo: number, typeWidget: string): void {
    this.avoService.getEventTriggerProps$()
      .pipe(
        take(1),
        tap(trigger => {
          this.AvoDispatch.selectBanner({
            ...this.avoService.avoBaseProperties(trigger),
            source: this.avoService.getAvoSourceValueType(trigger?.EVENT_PROPS?.SOURCE),
            titleSection,
            titlePromo,
            indexPromo,
            indexSection,
            typeWidget
          })
        })
      ).subscribe().unsubscribe();
  }
  
  public toggleFinancialPartners(
    event: { partnerInfo: FinancialPartnersInfoVM | null, searchBy: SearchBy | null }): void {
    this.financialPartnersInfo = event?.partnerInfo;
    this.showFinancialPartners = !this.showFinancialPartners;
    this.searchBySelected = event?.searchBy;
  }

  private scrollToSection(): void {
    const htmlId = this.route?.snapshot?.queryParamMap?.get(queryParamaHtmlId);
    this.scrollToSectionHtmlId(htmlId);
  }

  public scrollToSectionHtmlId(htmlId: string): void {
    if (htmlId) {
      let sectionAnchor = null;
      const listNodes = this.homeSectionList?.nativeElement?.childNodes;
      for (const section of listNodes) {
        if (section.id === htmlId) {
          sectionAnchor = section;
        }
      }
      if (sectionAnchor) {
        setTimeout(() => {
          window.scrollTo({
            left: 0, top:
              this.isMobile ? sectionAnchor?.offsetTop + (sectionAnchor?.offsetHeight / 2)
                : sectionAnchor?.offsetTop + sectionAnchor?.offsetHeight, behavior: 'smooth'
          });
        }, this.isMobile ? HALF_ONE_SECOND_MILLISECONDS : 0);
      }
    }
  }

  public toggleShowSwipeUp(): void {
    this.iframeWebView.nativeElement.src = '';
    this.modalWebView.nativeElement.style.display = 'none';
    this.loadingIframe = true;
    this.iframeHasBeenRequested = false;
  }
  public loadIframe(): void {
    this.loadingIframe = false;
    this.iframeHasBeenRequested = false;
  }
  public openIframe(url: string): void {
    if (this.isMobile) {
      this.iframeHasBeenRequested = true;
      this.modalWebView.nativeElement.style.display = 'block';
      this.iframeWebView.nativeElement.src = url;
    } else {
      window.open(url, '_blank');
    }
  }
}
